import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { filter } from 'rxjs';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { AssetStore } from '../../../../../stores/asset.store';
import { UnitStore } from '../../../../../stores/unit.store';
import { MenuItemWithIcon } from '../../../../../types/menu.interface';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { assetWriteSignal } from '../../../../../utils/asset-permissions.utils';
import { MeasureTab, RouteUtils } from '../../../../../utils/route.utils';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesMenuButtonComponent, CesMenuButtonSecondaryComponent } from '../../../../ui/ces-buttons';
import { ManualMeasureListComponent } from './manual/manual-measure-list.component';

@Component({
  selector: 'app-measures',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesAssetBreadcrumbComponent,
    CesMenuButtonComponent,
    CesMenuButtonSecondaryComponent,
    HasPermissionDirective,
    RouterLink,
    RouterOutlet,
    TabsModule,
    TranslocoModule,
  ],
  templateUrl: './measures.component.html',
})
export class MeasuresComponent implements OnInit {
  protected readonly PermissionScope = PermissionScope;

  protected readonly assetStore = inject(AssetStore);
  protected readonly unitStore = inject(UnitStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly activeTab = signal<string>('manual');
  readonly currentAssetId = signal<number | null>(null);

  readonly createMenuitems: Signal<MenuItem[]> = computed(() => {
    this.translations();
    return [
      { label: this.translate.translate('entities.measure.types.manual'), command: () => this.navigateToCreate('manual') },
      { label: this.translate.translate('entities.measure.types.automatic'), command: () => this.navigateToCreate('automatic') },
      {
        label: this.translate.translate('entities.measure.types.auscultationInfo'),
        command: () => this.navigateToCreate('auscultation-information'),
      },
      {
        label: this.translate.translate('entities.measure.types.analysisPost'),
        command: () => this.navigateToCreate('analysis-post'),
      },
    ];
  });

  // The active tab owns the export; the kebab just triggers it (same outlet-component
  // bridge as the campaign model detail page). Distinct from `activeTab`, which carries
  // the p-tabs label.
  private readonly manualTab = signal<ManualMeasureListComponent | null>(null);

  private readonly canWrite = assetWriteSignal(PermissionScope.Measures, this.currentAssetId);

  onTabActivate(component: unknown): void {
    this.manualTab.set(component instanceof ManualMeasureListComponent ? component : null);
  }

  onTabDeactivate(): void {
    this.manualTab.set(null);
  }

  readonly actionsMenuItems: Signal<MenuItemWithIcon[]> = computed(() => {
    this.translations();
    const manualTab = this.manualTab();
    return [
      {
        label: this.translate.translate('measureOrder.title'),
        materialIcon: 'swap_vert',
        visible: this.canWrite(),
        command: () => this.navigateToOrder(),
      },
      {
        label: this.translate.translate('barcodeExport.title'),
        materialIcon: 'download',
        visible: manualTab !== null,
        disabled: manualTab?.isExporting() ?? false,
        command: () => void manualTab?.exportBarcodes(),
      },
    ];
  });

  readonly breadcrumbTrail: Signal<MenuItem[]> = computed(() => {
    this.translations();
    return [{ label: this.translate.translate('navigation.measures') }];
  });

  ngOnInit(): void {
    this.activeTab.set(RouteUtils.extractActiveTabFromUrl(this.router.url));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => this.activeTab.set(RouteUtils.extractActiveTabFromUrl(event.urlAfterRedirects)));

    this.route.parent?.parent?.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params['id'] ? Number(params['id']) : null;

      if (id && id > 0) {
        this.currentAssetId.set(id);
        void this.assetStore.loadById(id);
        void this.unitStore.loadAll();
      } else {
        this.currentAssetId.set(null);
      }
    });
  }

  private navigateToCreate(measureType: MeasureTab): void {
    const assetId = this.currentAssetId();
    if (!assetId) return;
    void this.router.navigate(RouteUtils.getMeasurePath(assetId, measureType, 'new'));
  }

  private navigateToOrder(): void {
    const assetId = this.currentAssetId();
    if (!assetId) return;
    void this.router.navigate(['/assets', assetId, 'configuration', 'measures', 'order']);
  }
}
